import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Bar, BarChart, Brush, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styles from './styles.module.scss';
import { ReactComponent as TravellerIcon } from './assets/TravellerIcon.svg';

import {
  ChartColorsMap,
  ChartHeightMobile,
  ChartSoilHeight,
  ChartYAxisWidth,
} from './constants/index.jsx';
import { DateTimeLlFormat, DateTimeMDFormat } from '../../constants/data';
import { BreakpointMobile } from '../../constants/ui';
import { useViewport } from '../../hooks';
import { ChartLegendSoil } from './index.jsx';
import { GetBrushPosition, GetRainTicks } from './lib/index.jsx';

const ChartTooltip = ({ t, active, payload, label }) => {
  if (active && payload && payload.length) {
    const payloadData = payload[0].payload;
    return (
      <div className={`chart-tooltip`}>
        <div className="chart-tooltip__header">{dayjs(label).format(DateTimeLlFormat)}</div>
        {payloadData && (
          <div key={'tooltip' + payloadData.value} className="chart-tooltip__item">
            <div className="chart-tooltip__title" style={{ color: ChartColorsMap.rain }}>
              <span>{t('weather.rain')}</span>
            </div>
            <div className="chart-tooltip__value">
              <span>{payloadData.value} %/h</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

const ChartRain = ({ t, period, data, chartsMap }) => {
  const { widthViewport } = useViewport();
  const [brushIndex, setBrushIndex] = useState([0, 0]);
  const [domainYAsis, setDomainYAsis] = useState([0, 0]);
  const [ticksYAsis, setTicksYAsis] = useState(null);

  const onChangeBrush = () => {};

  useEffect(() => {
    if (!data || !data.data) return;

    // Update brush position
    setBrushIndex(GetBrushPosition({ data: data.data, period }));

    const values = [];
    data.data.map((item) => {
      values.push(item.value);
    });

    const ticks = GetRainTicks({ values, steps: 5 });
    setTicksYAsis(ticks.ticks);
    setDomainYAsis([ticks.min, ticks.max]);
  }, [period, data]);

  return (
    <div className={styles.chartContainer}>
      {/*Legend*/}
      <ChartLegendSoil t={t} title={chartsMap.rain.title} units={chartsMap.rain.units} />

      {/*Chart*/}
      {data && (
        <ResponsiveContainer
          width="100%"
          height={widthViewport > BreakpointMobile ? ChartSoilHeight : ChartHeightMobile}
        >
          <BarChart
            className={'rechart-soil'}
            width={800}
            height={widthViewport > BreakpointMobile ? ChartSoilHeight : ChartHeightMobile}
            data={[...data.data]}
            margin={{ top: 2, right: 28 }}
            syncId="soilRainId"
          >
            <CartesianGrid strokeDasharray="0 0" stroke={ChartColorsMap.grid} vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              minTickGap={60}
              height={50}
              tickSize={12}
              tickFormatter={(value) => dayjs(value).format(DateTimeMDFormat)}
            />
            <YAxis
              yAxisId="left"
              width={ChartYAxisWidth}
              ticks={ticksYAsis}
              tickCount={2}
              tickSize={0}
              tickMargin={10}
              domain={domainYAsis ? domainYAsis : [0, 100]}
            />
            <Tooltip content={<ChartTooltip t={t} />} cursor={false} />
            <Bar
              yAxisId="left"
              dataKey={`value`}
              fill={ChartColorsMap.rain}
              activeBar={{ fill: ChartColorsMap.rainActive }}
            />
            <Brush
              height={40}
              stroke={ChartColorsMap.brush}
              dataKey={'date'}
              tickFormatter={(value) => dayjs(value).format('MMM DD')}
              startIndex={brushIndex[0]}
              endIndex={brushIndex[1]}
              alwaysShowText={true}
              onChange={(newIndex) => onChangeBrush(newIndex)}
              traveller={<TravellerIcon />}
              travellerWidth={14}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default ChartRain;
